import React, { useEffect, useState } from "react";
import { collection, onSnapshot, orderBy, query, getCountFromServer } from "firebase/firestore";
import { db } from "../../firebase";

export default function EISSidebar({ onSelect, onOpenMaster }) {
  const [brands, setBrands] = useState({});
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedModelId, setSelectedModelId] = useState(null);
  const [modelMetadata, setModelMetadata] = useState({}); // { [modelId]: { status: 'green'|'yellow'|'red', r: 0, p: 0 } }
  const [isCollapsed, setIsCollapsed] = useState(false);

  useEffect(() => {
    // 1. Fetch Brands
    const unsub = onSnapshot(collection(db, "EIS"), (snap) => {
      const temp = {};
      snap.forEach((doc) => {
        temp[doc.id] = [];
      });
      setBrands(prev => ({ ...prev, ...temp }));

      // 2. Fetch Models for each Brand
      Object.keys(temp).forEach(brand => {
        const q = query(collection(db, "EIS", brand, "Models"));
        onSnapshot(q, async (mSnap) => {
          // Fetch full data for variants support
          const models = mSnap.docs.map(d => ({ id: d.id, ...d.data() }));

          // Calculate counts and status
          // Note: utilizing getCountFromServer for optimization, but it's still N*2 reads per brand reload.
          // In a larger app, this should be pre-aggregated in a stats document.
          const newMetadata = {};

          // Parallel fetch for speed
          await Promise.all(models.map(async (m) => {
            try {
              const rawSnap = await getCountFromServer(collection(db, "EIS", brand, "Models", m.id, "RawData"));
              const paramSnap = await getCountFromServer(collection(db, "EIS", brand, "Models", m.id, "ParameterIDs"));

              const r = rawSnap.data().count;
              const p = paramSnap.data().count;

              let status = 'red'; // Default
              if (r > 0 && p > 0) status = 'green';
              else if (r > 0 || p > 0) status = 'yellow';

              newMetadata[m.id] = { status, r, p };
            } catch (e) {
              console.error("Error fetching stats for", m.id, e);
              newMetadata[m.id] = { status: 'gray', r: 0, p: 0 };
            }
          }));

          setModelMetadata(prev => ({ ...prev, ...newMetadata }));
          setBrands(prev => ({ ...prev, [brand]: models }));
          setLoading(false);
        });
      });
    });

    return () => unsub();
  }, []);

  const toggleBrand = (brand) => {
    setExpanded(prev => ({ ...prev, [brand]: !prev[brand] }));
  };

  const handleSelect = (brand, model) => {
    setSelectedModelId(model);
    onSelect(brand, model);
  }

  // Filter Logic
  const filteredBrands = Object.entries(brands).reduce((acc, [brand, models]) => {
    const matchesBrand = brand.toLowerCase().includes(searchTerm.toLowerCase());

    // 1. Find directly matching models
    const directMatches = models.filter(m => m.id.toLowerCase().includes(searchTerm.toLowerCase()));

    // 2. If we have direct matches that are variants, ensure we include their base models
    const relatedBaseModels = [];
    directMatches.forEach(m => {
      if (m.variant && m.modelName) {
        const base = models.find(b => b.id === m.modelName);
        if (base && !directMatches.find(dm => dm.id === base.id)) {
          relatedBaseModels.push(base);
        }
      }
    });

    // 3. Combine matches
    let finalModels = [...directMatches, ...relatedBaseModels];

    // Remove duplicates
    finalModels = finalModels.filter((v, i, a) => a.findIndex(t => (t.id === v.id)) === i);

    if (matchesBrand) {
      finalModels = models; // Show all if brand matches
    }

    if (matchesBrand || finalModels.length > 0) {
      acc[brand] = finalModels;
    }
    return acc;
  }, {});

  // Group models for a brand
  const getGroupedModels = (models) => {
    // Ensure we treat models as objects
    const baseModels = models.filter(m => !m.variant).sort((a, b) => a.id.localeCompare(b.id));
    const variants = models.filter(m => m.variant);

    return { baseModels, variants };
  };

  const renderModelList = (brand, models) => {
    const { baseModels, variants } = getGroupedModels(models);

    return (
      <div className="bg-white p-1.5 space-y-0.5 animate-slide-down">
        {baseModels.map(base => {
          const isActive = selectedModelId === base.id;
          const myVariants = variants.filter(v => v.modelName === base.id).sort((a, b) => {
            // Sort variants by createdAt if available, else name
            const tA = a.createdAt?.seconds || 0;
            const tB = b.createdAt?.seconds || 0;
            return tB - tA;
          });

          return (
            <div key={base.id}>
              {/* Base Model */}
              <button
                onClick={() => handleSelect(brand, base.id)}
                className={`
                                w-full text-left px-3 py-2 rounded-lg text-xs font-medium transition-all flex items-center justify-between group
                                ${isActive
                    ? 'bg-indigo-50 text-indigo-700 font-bold border border-indigo-100 shadow-sm'
                    : `hover:bg-slate-50 border border-transparent ${getStatusColor(modelMetadata[base.id]?.status)}`}
                            `}
              >
                <span>{base.id}</span>
                <div className="flex items-center gap-2">
                  {/* Status Dot */}
                  <span className={`w-1.5 h-1.5 rounded-full ${getStatusColor(modelMetadata[base.id]?.status, false)}`}></span>
                  {isActive && <span className="w-1.5 h-1.5 rounded-full bg-indigo-500"></span>}
                </div>
              </button>

              {/* Variants */}
              {myVariants.length > 0 && (
                <div className="ml-3 pl-3 border-l-2 border-slate-100 space-y-0.5 mt-0.5 mb-1">
                  {myVariants.map(variant => {
                    const isVarActive = selectedModelId === variant.id;
                    return (
                      <button
                        key={variant.id}
                        onClick={() => handleSelect(brand, variant.id)}
                        className={`
                                                    w-full text-left px-2 py-1.5 rounded-md text-[11px] font-medium transition-all flex items-center justify-between group relative
                                                    ${isVarActive
                            ? 'bg-purple-50 text-purple-700 font-bold'
                            : `hover:bg-slate-50 ${getStatusColor(modelMetadata[variant.id]?.status)}`}
                                                `}
                      >
                        <div className="flex items-center gap-1.5">
                          {/* Status Indicator for Variant */}
                          <span className={`w-1 h-1 rounded-full ${getStatusColor(modelMetadata[variant.id]?.status, false)}`}></span>
                          <div className="w-1.5 h-px bg-slate-300"></div>
                          {variant.id}
                        </div>
                        {isVarActive && <span className="w-1 h-1 rounded-full bg-purple-500"></span>}
                      </button>
                    )
                  })}
                </div>
              )}
            </div>
          );
        })}

        {/* Orphan Variants (safeguard) */}
        {variants.filter(v => !baseModels.find(b => b.id === v.modelName)).map(orphan => (
          <button
            key={orphan.id}
            onClick={() => handleSelect(brand, orphan.id)}
            className={`
                            w-full text-left px-3 py-2 rounded-lg text-xs font-medium transition-all flex items-center justify-between group text-orange-500
                             ${selectedModelId === orphan.id ? 'bg-orange-50 font-bold' : ''}
                        `}
          >
            <span>{orphan.id} <span className="text-[9px] opacity-50 ml-1">(Variant)</span></span>
          </button>
        ))}

        {models.length === 0 && (
          <div className="p-3 text-[10px] text-slate-400 italic text-center">No models available</div>
        )}
      </div>
    );
  };

  // Helper to get brand stats
  const getBrandStats = (brand, models) => {
    let stats = { green: 0, yellow: 0, red: 0 };
    models.forEach(m => {
      const st = modelMetadata[m.id]?.status || 'red';
      if (st === 'green') stats.green++;
      else if (st === 'yellow') stats.yellow++;
      else stats.red++;
    });
    return stats;
  };

  const getStatusColor = (status, isText = true) => {
    switch (status) {
      case 'green': return isText ? 'text-green-600' : 'bg-green-500';
      case 'yellow': return isText ? 'text-yellow-600' : 'bg-yellow-500';
      default: return isText ? 'text-slate-400' : 'bg-slate-300';
    }
  };

  return (
    <>
      {/* Collapsed State Toggle Button (when hidden) - optional or just handle main div width */}
      <div className={`${isCollapsed ? 'w-12' : 'w-80'} h-full bg-white border-r border-slate-200 flex flex-col shadow-sm z-10 transition-all duration-300 relative`}>

        {/* Collapse Toggle */}
        <button
          onClick={() => setIsCollapsed(!isCollapsed)}
          className="absolute -right-3 top-6 bg-white border border-slate-200 shadow-sm rounded-full p-1 text-slate-400 hover:text-indigo-600 z-50"
        >
          <svg className={`w-3 h-3 transition-transform ${isCollapsed ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>

        {isCollapsed ? (
          <div className="flex flex-col items-center pt-6 space-y-4">
            <span className="text-indigo-600 font-black text-xl">E</span>
            <div className="w-8 h-px bg-slate-100"></div>
            {/* Icons of brands could go here, for now just simple collapsed state */}
          </div>
        ) : (
          <>
            {/* 1. Sidebar Header */}
            <div className="p-5 border-b border-slate-100 bg-white sticky top-0 z-20">
              <div className="flex items-center justify-between mb-4">

                <div>
                  <h1 className="text-lg font-black text-slate-800 tracking-tight leading-none">EIS <span className="text-indigo-600">HUB</span></h1>
                  <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Engineering System</span>
                </div>

                <button
                  onClick={onOpenMaster}
                  className="p-1.5 rounded-lg bg-indigo-50 text-indigo-600 hover:bg-indigo-100 transition-colors"
                  title="Open OBD Master Chart"
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                </button>
              </div>

              {/* Search */}
              <div className="relative group">
                <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 group-focus-within:text-indigo-500 transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
                <input
                  type="text"
                  placeholder="Search models..."
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl py-2.5 pl-10 pr-4 text-xs font-bold text-slate-700 outline-none focus:ring-2 focus:ring-indigo-100 focus:border-indigo-500 transition-all placeholder:font-medium placeholder:text-slate-400"
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    // Auto expand all when searching
                    if (e.target.value.length > 0) {
                      const allExpanded = {};
                      Object.keys(brands).forEach(b => allExpanded[b] = true);
                      setExpanded(allExpanded);
                    } else {
                      setExpanded({});
                    }
                  }}
                />
              </div>
            </div>

            {/* 2. Scrollable List */}
            <div className="flex-1 overflow-y-auto p-4 space-y-2 custom-scrollbar">
              {loading ? (
                <div className="flex flex-col items-center justify-center h-40 gap-3">
                  <div className="w-6 h-6 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin"></div>
                  <span className="text-xs font-bold text-slate-400">Syncing Database...</span>
                </div>
              ) : (
                Object.keys(filteredBrands).length === 0 ? (
                  <div className="text-center py-10">
                    <span className="text-xs font-semibold text-slate-400">No matching models found.</span>
                  </div>
                ) : (
                  Object.keys(filteredBrands).map(brand => (
                    <div key={brand} className="border border-slate-100 rounded-xl overflow-hidden bg-white shadow-sm hover:shadow transition-shadow">
                      {/* Accordion Header */}
                      <button
                        onClick={() => toggleBrand(brand)}
                        className="w-full flex items-center justify-between p-3 bg-slate-50/50 hover:bg-slate-50 transition-colors border-b border-slate-50"
                      >
                        <div className="flex items-center gap-2">
                          <span className={`w-1.5 h-1.5 rounded-full ${expanded[brand] ? 'bg-indigo-500' : 'bg-slate-300'}`}></span>
                          <span className="text-xs font-bold text-slate-700 uppercase tracking-wide">{brand}</span>

                          {/* Summary Counts */}
                          {!expanded[brand] && (
                            <div className="flex items-center gap-1 ml-2">
                              {(() => {
                                const stats = getBrandStats(brand, filteredBrands[brand]);
                                return (
                                  <>
                                    {stats.green > 0 && <span className="text-[9px] text-green-600 bg-green-50 px-1 rounded flex items-center gap-0.5"><span className="w-1 h-1 bg-green-500 rounded-full"></span>{stats.green}</span>}
                                    {stats.yellow > 0 && <span className="text-[9px] text-yellow-600 bg-yellow-50 px-1 rounded flex items-center gap-0.5"><span className="w-1 h-1 bg-yellow-500 rounded-full"></span>{stats.yellow}</span>}
                                    {stats.red > 0 && <span className="text-[9px] text-slate-400 bg-slate-100 px-1 rounded flex items-center gap-0.5"><span className="w-1 h-1 bg-slate-400 rounded-full"></span>{stats.red}</span>}
                                  </>
                                )
                              })()}
                            </div>
                          )}

                          <span className="bg-slate-200 text-slate-500 px-1.5 py-0.5 rounded text-[9px] font-bold ml-auto">{filteredBrands[brand].length}</span>
                        </div>
                        <svg
                          className={`w-4 h-4 text-slate-400 transition-transform duration-200 ${expanded[brand] ? 'rotate-180' : ''}`}
                          fill="none" viewBox="0 0 24 24" stroke="currentColor"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </button>

                      {/* Accordion Content */}
                      {expanded[brand] && renderModelList(brand, filteredBrands[brand])}
                    </div>
                  ))
                )
              )}
            </div>

            {/* Footer */}
            <div className="p-4 border-t border-slate-100 bg-slate-50/50 text-[10px] text-slate-400 font-medium text-center">
              EIS v2.1 • SkillWheel
            </div>
          </>
        )}
      </div>
    </>
  );
}
